import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, Star } from "lucide-react";
import api from "@/lib/api";
import { StarRating } from "@/components/StarRating";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, delay: d },
});

export function ReviewsSection({ limit = 6, showCta = true, heading = "What our customers say" }) {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });

  useEffect(() => {
    let alive = true;
    api.get("/reviews").then(({ data }) => { if (alive) setData(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reviews = (data.reviews || []).slice(0, limit);

  return (
    <section className="max-w-[1720px] mx-auto px-5 sm:px-10 py-16 sm:py-24" data-testid="reviews-section">
      <motion.div {...fade(0)} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="text-sm uppercase tracking-[0.2em] text-gold">Reviews</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">{heading}</h2>
        </div>
        {data.count > 0 && (
          <div className="flex items-center gap-3" data-testid="reviews-average">
            <span className="font-heading text-4xl font-bold text-gold">{data.average.toFixed(1)}</span>
            <div>
              <StarRating value={data.average} />
              <div className="text-xs text-slate-400 mt-1">{data.count} review{data.count !== 1 ? "s" : ""}</div>
            </div>
          </div>
        )}
      </motion.div>

      {reviews.length === 0 ? (
        <motion.div {...fade(0.05)} className="mt-8 rounded-sm border border-gold/15 bg-navy p-8 text-center" data-testid="reviews-empty">
          <div className="flex justify-center gap-1 text-gold mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-gold" />)}</div>
          <p className="text-slate-300">Be the first to share your experience with Bridge Taxi.</p>
          {showCta && <Link to="/review" data-testid="reviews-empty-cta" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200">Leave a review</Link>}
        </motion.div>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r, i) => (
            <motion.div key={r.id} {...fade(0.05 + (i % 3) * 0.06)} data-testid={`review-card-${r.id}`}
              className="relative rounded-sm border border-gold/15 bg-navy p-6 transition-colors duration-200 hover:border-gold/45">
              <Quote className="absolute top-5 right-5 h-8 w-8 text-gold/15" />
              <StarRating value={r.rating} />
              {r.comment && <p className="mt-4 text-slate-200 leading-relaxed italic">“{r.comment}”</p>}
              <div className="mt-5 pt-4 border-t border-gold/10">
                <div className="font-heading text-white font-semibold">{r.name}</div>
                {r.service_type && <div className="text-xs text-gold/70 uppercase tracking-wider mt-0.5">{r.service_type}</div>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showCta && reviews.length > 0 && (
        <motion.div {...fade(0.1)} className="mt-10 text-center">
          <Link to="/review" data-testid="reviews-cta" className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-white hover:border-gold hover:text-gold transition-colors duration-200">
            <Star className="h-4 w-4 text-gold fill-gold" /> Share your experience
          </Link>
        </motion.div>
      )}
    </section>
  );
}
